import {
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import type { Location } from 'react-router-dom';
import {
    NavLink,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    BarChart3,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    FileText,
    LayoutDashboard,
    LogOut,
    Megaphone,
    MessageSquare,
    Package,
    RotateCcw,
    Settings,
    ShoppingCart,
    Users,
    X,
} from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import AuthContext from '@/contexts/auth';
import { useChatUnread } from '@/features/chat/context';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRbac } from '@/hooks/useRbac';
import { baseRequest } from '@/lib/base';
import type { ApiNotification } from '@/lib/notifications-api';
import { fetchNotifications } from '@/lib/notifications-api';
import {
    NAMESPACES,
    PERMISSIONS,
} from '@/lib/rbac';
import { extractArray } from '@/lib/shared-utils';
import { cn } from '@/lib/utils';
import { fetchReturns } from '@/pages/Return/api';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileMenuOpen?: boolean;
  onCloseMobileMenu?: () => void;
}

interface NavChild {
    name: string;
    href: string;
    permission?: string;
    namespaces?: string[];
    // Overrides react-router's own isActive check, which only compares
    // pathname. Needed for siblings that share a pathname and differ only
    // by query string (e.g. Return's Overview vs Incoming Returns tabs).
    isActive?: (location: Location) => boolean;
}

interface NavItem {
    name: string;
    href?: string;
    icon: React.ComponentType<{ className?: string }>;
    namespaces?: string[];
    permission?: string;
    children?: NavChild[];
}

const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    {
        name: 'Chat', href: '/chat', icon: MessageSquare, namespaces: [NAMESPACES.whatsapp],
    },
    {
        name: 'Related Parties',
        icon: Users,
        namespaces: [NAMESPACES.relatedParty],
        children: [
            { name: 'Overview', href: '/related-parties' },
            { name: 'My Parties', href: '/related-parties/my-parties' },
            {
                name: 'Add New',
                href: '/related-parties/add',
                permission: PERMISSIONS.relatedPartyConnect,
            },
            { name: 'Add Manual Party', href: '/related-parties/manual' },
            { name: 'Find on Map', href: '/related-parties/map' },
            { name: 'Requests', href: '/related-parties/requests' },
            { name: 'Favorites', href: '/related-parties/favorites' },
            { name: 'Blocked', href: '/related-parties/blocked' },
        ],
    },
    {
        name: 'Inventory',
        icon: Package,
        namespaces: [
            NAMESPACES.inventoryProduct,
            NAMESPACES.inventoryCategory,
            NAMESPACES.inventoryGroup,
            NAMESPACES.inventoryUnit,
        ],
        children: [
            {
                name: 'Overview',
                href: '/inventory',
                namespaces: [
                    NAMESPACES.inventoryProduct,
                    NAMESPACES.inventoryCategory,
                    NAMESPACES.inventoryGroup,
                    NAMESPACES.inventoryUnit,
                ],
            },
            {
                name: 'Products',
                href: '/inventory/products',
                namespaces: [NAMESPACES.inventoryProduct],
            },
            {
                name: 'Groups',
                href: '/inventory/groups',
                namespaces: [NAMESPACES.inventoryGroup],
            },
            {
                name: 'Categories',
                href: '/inventory/categories',
                namespaces: [NAMESPACES.inventoryCategory],
            },
            {
                name: 'Units',
                href: '/inventory/units',
                namespaces: [NAMESPACES.inventoryUnit],
            },
        ],
    },
    {
        name: 'Orders',
        icon: ShoppingCart,
        namespaces: [NAMESPACES.order],
        children: [
            { name: 'Overview', href: '/orders' },
            { name: 'Create Order', href: '/orders/create' },
            { name: 'Incoming Orders', href: '/orders/incoming' },
            { name: 'Outgoing Orders', href: '/orders/outgoing' },
            { name: 'Saved Orders', href: '/orders/saved' },
        ],
    },
    {
        name: 'Product Quotation',
        icon: FileText,
        namespaces: [NAMESPACES.quotation],
        children: [
            { name: 'Request for Quotation', href: '/quotations/request' },
            { name: 'Respond to Quotation', href: '/quotations/respond' },
        ],
    },
    {
        name: 'Schemes & Offers',
        icon: Megaphone,
        namespaces: [NAMESPACES.announcement],
        children: [
            { name: 'Billboard', href: '/announcements' },
            {
                name: 'Create New',
                href: '/announcements/create',
                permission: PERMISSIONS.announcementCreate,
            },
        ],
    },
    {
        name: 'Return',
        icon: RotateCcw,
        namespaces: [NAMESPACES.order],
        children: [
            {
                name: 'Overview',
                href: '/return',
                isActive: (loc) => loc.pathname === '/return',
            },
            { name: 'Incoming Returns', href: '/return/incoming' },
            { name: 'Outgoing Returns', href: '/return/outgoing' },
            {
                name: 'Order Return',
                href: '/return/order-return',
                permission: PERMISSIONS.orderRequestReturn,
            },
        ],
    },
    {
        name: 'Reports',
        href: '/reports',
        icon: BarChart3,
        namespaces: [NAMESPACES.report],
    },
];

function formatBadgeCount(count: number): string {
    if (count > 99) return '99+';
    return String(count);
}

// An order notification is "incoming" (needs a supplier response) when its
// action is one of these; everything else is an update to an order the
// current user placed as a customer ("outgoing"). Mirrors the routing logic
// in NotificationsMenu.tsx's getNotificationRoute.
const SUPPLIER_SIDE_ACTIONS = ['created', 'customer_accepted', 'customer_rejected', 'pending'];

function isIncomingOrderNotification(n: ApiNotification): boolean {
    const payload = n.payload as Record<string, unknown> | null;
    const action = (payload?.action as string) ?? n.kind.replace('order_', '');
    return SUPPLIER_SIDE_ACTIONS.includes(action);
}

function Sidebar({
    isCollapsed,
    onToggle,
    isMobileMenuOpen = false,
    onCloseMobileMenu,
}: SidebarProps) {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const location = useLocation();
    const { logout: userLogout } = useContext(AuthContext);
    const { can, hasAnyNamespace, rbac } = useRbac();
    const canSeeReturns = hasAnyNamespace([NAMESPACES.order]);
    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

    const { data: notifRes } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => fetchNotifications({ page_size: 20 }),
        refetchInterval: 30000,
    });
    const unreadOrderNotifications = (extractArray<ApiNotification>(notifRes?.data))
        .filter((n) => !n.is_read && typeof n.kind === 'string' && n.kind.startsWith('order_'));
    const unreadOrderCount = unreadOrderNotifications.length;
    const unreadIncomingCount = unreadOrderNotifications.filter(isIncomingOrderNotification).length;
    const unreadOutgoingCount = unreadOrderCount - unreadIncomingCount;
    const { totalUnread: unreadChatCount } = useChatUnread();

    // Returns have no notification feed yet, so the badge counts pending
    // incoming returns directly — "return requests waiting on your decision".
    const { data: returnsRes } = useQuery({
        queryKey: ['returns', 'supplier', 'pending', 'badge-count'],
        queryFn: () => fetchReturns({
            role: 'supplier', status: 'pending', page: 1, pageSize: 1,
        }),
        enabled: canSeeReturns,
    });
    const pendingIncomingReturnsCount = returnsRes?.count ?? 0;

    const childBadgeCounts: Record<string, number> = {
        'Incoming Orders': unreadIncomingCount,
        'Outgoing Orders': unreadOutgoingCount,
        'Incoming Returns': pendingIncomingReturnsCount,
    };
    const topLevelBadgeCounts: Record<string, number> = {
        Orders: unreadOrderCount,
        Chat: unreadChatCount,
        Return: pendingIncomingReturnsCount,
    };

    const isChildVisible = (c: NavChild) => {
        if (c.permission && !can(c.permission)) return false;
        if (c.namespaces && !hasAnyNamespace(c.namespaces)) return false;
        return true;
    };

    const isItemVisible = (item: NavItem) => {
        if (item.permission && !can(item.permission)) return false;
        if (item.namespaces && !hasAnyNamespace(item.namespaces)) return false;
        return true;
    };

    // While whoami is hydrating we leave the sidebar empty rather than
    // flashing the full set and then collapsing it.
    const visibleNavigation = rbac === null
        ? navigation.filter((item) => !item.permission && !item.namespaces)
        : navigation
            .map((item) => {
                if (!item.children) return item;
                const visibleChildren = item.children.filter(isChildVisible);
                return { ...item, children: visibleChildren };
            })
            .filter((item) => {
                if (!isItemVisible(item)) return false;
                if (item.children) return item.children.length > 0;
                return true;
            });

    const isPathActive = useCallback((href: string) => (
        location.pathname === href || location.pathname.startsWith(`${href}/`)
    ), [location.pathname]);

    // Auto-expand menus when a child route is active
    useEffect(() => {
        const toExpand = navigation
            .filter((item) => item.children?.some((c) => isPathActive(c.href)))
            .reduce<Record<string, boolean>>((acc, item) => {
                acc[item.name] = true;
                return acc;
            }, {});
        if (Object.keys(toExpand).length > 0) {
            setExpandedMenus((prev) => ({ ...prev, ...toExpand }));
        }
    }, [isPathActive]);

    const handleNavClick = useCallback(() => {
        if (isMobile && onCloseMobileMenu) {
            onCloseMobileMenu();
        }
    }, [isMobile, onCloseMobileMenu]);

    const toggleSubmenu = useCallback((name: string) => {
        setExpandedMenus((prev) => ({ ...prev, [name]: !prev[name] }));
    }, []);

    const handleLogout = useCallback(async () => {
        try {
            const response = await baseRequest({
                url: '/system/auth/logout/',
                method: 'GET',
            });
            if (response?.data?.status) {
                userLogout();
                toast.success('Logout successful');
            } else {
                toast.error(response?.data?.data?.logout?.error || 'Logout failed');
            }
        } catch (error: unknown) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'An error occurred during logout',
            );
        }
    }, [userLogout]);

    const renderNavItem = (item: NavItem, collapsed: boolean, onNav?: () => void) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedMenus[item.name];
        const childIsActive = hasChildren && item.children!.some(
            (child) => isPathActive(child.href),
        );
        const topLevelBadgeCount = topLevelBadgeCounts[item.name] ?? 0;

        if (hasChildren) {
            return (
                <li key={item.name}>
                    <button
                        type="button"
                        onClick={() => {
                            if (collapsed) {
                                navigate(item.children![0].href);
                                onNav?.();
                            } else {
                                toggleSubmenu(item.name);
                            }
                        }}
                        className={cn(
                            'relative flex items-center w-full px-2.5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                            'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                            childIsActive && 'bg-sidebar-accent text-sidebar-accent-foreground',
                            collapsed ? 'justify-center' : 'justify-between',
                        )}
                    >
                        <div className="flex items-center">
                            <item.icon className={cn('h-5 w-5', !collapsed && 'mr-3')} />
                            {!collapsed && <span>{item.name}</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                            {topLevelBadgeCount > 0 && (
                                <span
                                    data-testid={`${item.name.toLowerCase()}-unread-badge`}
                                    className={cn(
                                        'inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none',
                                        collapsed
                                            && 'absolute top-1 right-1 h-2 w-2 p-0',
                                        !collapsed && 'h-5 min-w-5 px-1.5',
                                    )}
                                    aria-label={`${topLevelBadgeCount} unread`}
                                >
                                    {!collapsed && formatBadgeCount(topLevelBadgeCount)}
                                </span>
                            )}
                            {!collapsed && (
                                <ChevronDown className={cn(
                                    'h-4 w-4 transition-transform duration-200',
                                    isExpanded && 'rotate-180',
                                )}
                                />
                            )}
                        </div>
                    </button>
                    {!collapsed && isExpanded && (
                        <ul className="ml-5 mt-1 space-y-0.5 border-l border-sidebar-border pl-3">
                            {item.children!.map((child) => {
                                const childBadgeCount = childBadgeCounts[child.name] ?? 0;
                                return (
                                    <li key={child.name}>
                                        <NavLink
                                            to={child.href}
                                            end
                                            onClick={onNav}
                                            className={({ isActive }) => {
                                                const active = child.isActive
                                                    ? child.isActive(location)
                                                    : isActive;
                                                return cn(
                                                    'flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm rounded-md transition-all duration-200',
                                                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                                    active
                                                        ? 'bg-sidebar-primary/15 text-sidebar-primary font-medium'
                                                        : 'text-sidebar-foreground/60',
                                                );
                                            }}
                                        >
                                            <span>{child.name}</span>
                                            {childBadgeCount > 0 && (
                                                <span
                                                    data-testid={`${child.name}-unread-badge`}
                                                    className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[9.5px] font-semibold leading-none text-primary-foreground"
                                                    aria-label={`${childBadgeCount} unread`}
                                                >
                                                    {formatBadgeCount(childBadgeCount)}
                                                </span>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </li>
            );
        }

        return (
            <li key={item.name}>
                <NavLink
                    to={item.href!}
                    onClick={onNav}
                    className={({ isActive }) => cn(
                        'flex items-center px-2.5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative',
                        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                        isActive
                            ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                            : 'text-sidebar-foreground/70',
                        collapsed ? 'justify-center' : 'justify-start',
                    )}
                >
                    <item.icon className={cn('h-5 w-5', !collapsed && 'mr-3')} />
                    {!collapsed && <span className="flex-1">{item.name}</span>}
                    {topLevelBadgeCount > 0 && (
                        <span
                            data-testid={`${item.name.toLowerCase()}-unread-badge`}
                            className={cn(
                                'inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none',
                                collapsed
                                    ? 'absolute top-1 right-1 h-2 w-2 p-0'
                                    : 'h-5 min-w-5 px-1.5',
                            )}
                            aria-label={`${topLevelBadgeCount} unread`}
                        >
                            {!collapsed && formatBadgeCount(topLevelBadgeCount)}
                        </span>
                    )}
                </NavLink>
            </li>
        );
    };

    const renderBottomSection = (collapsed: boolean, onNav?: () => void) => (
        <div className="border-t border-sidebar-border px-3 py-4 space-y-0.5">
            <NavLink
                to="/settings"
                onClick={onNav}
                className={({ isActive }) => cn(
                    'flex items-center px-2.5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/70',
                    collapsed ? 'justify-center' : 'justify-start',
                )}
            >
                <Settings className={cn('h-5 w-5', !collapsed && 'mr-3')} />
                {!collapsed && <span>Settings</span>}
            </NavLink>
            <button
                type="button"
                onClick={() => {
                    onNav?.();
                    setLogoutConfirmOpen(true);
                }}
                className={cn(
                    'flex items-center w-full px-2.5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    'text-sidebar-foreground/70 hover:bg-destructive/15 hover:text-destructive',
                    collapsed ? 'justify-center' : 'justify-start',
                )}
            >
                <LogOut className={cn('h-5 w-5', !collapsed && 'mr-3')} />
                {!collapsed && <span>Logout</span>}
            </button>
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <div className={cn(
                'hidden md:flex relative bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 ease-in-out',
                isCollapsed ? 'w-16' : 'w-64',
            )}
            >
                <div className="flex flex-col h-full w-full">
                    {/* Header */}
                    <div className="h-[60px] px-4 border-b border-sidebar-border flex items-center">
                        <div className="flex items-center justify-between w-full">
                            {!isCollapsed && (
                                <img
                                    src="/chanco-horizontal.png"
                                    alt="Chanco"
                                    className="h-10 w-auto dark:brightness-0 dark:invert"
                                />
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onToggle}
                                className="p-2 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            >
                                {isCollapsed ? (
                                    <ChevronRight className="h-4 w-4" />
                                ) : (
                                    <ChevronLeft className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-4 overflow-y-auto subtle-scrollbar">
                        <ul className="space-y-0.5">
                            {visibleNavigation.map((item) => renderNavItem(item, isCollapsed))}
                        </ul>
                    </nav>

                    {/* Bottom Section */}
                    {renderBottomSection(isCollapsed)}
                </div>
            </div>

            {/* Mobile Sidebar */}
            <div className={cn(
                'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar text-sidebar-foreground border-r border-sidebar-border transform transition-transform duration-300 ease-in-out md:hidden',
                isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
            )}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="h-[60px] px-4 border-b border-sidebar-border flex items-center">
                        <div className="flex items-center justify-between w-full">
                            <img
                                src="/chanco-horizontal.png"
                                alt="Chanco"
                                className="h-10 w-auto brightness-0 invert"
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onCloseMobileMenu}
                                className="p-2 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-4 overflow-y-auto subtle-scrollbar">
                        <ul className="space-y-0.5">
                            {visibleNavigation.map(
                                (item) => renderNavItem(item, false, handleNavClick),
                            )}
                        </ul>
                    </nav>

                    {/* Bottom Section */}
                    {renderBottomSection(false, handleNavClick)}
                </div>
            </div>

            <AlertDialog
                open={logoutConfirmOpen}
                onOpenChange={setLogoutConfirmOpen}
            >
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Log out?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to log out? You will need
                            to sign in again to continue.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleLogout}
                            className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Log out
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export default Sidebar;
